/**
 * Game.ts — The world, the loop, and the objective state machine.
 *
 * Owns the Three.js scene, the entities and systems, and drives:
 *
 *   WALKING → (dog squats, poops, and the walker bags it) → CARRYING_BAG
 *           → (reach bin) → GO_HOME → (reach home zone) → WON
 *
 * …plus the two terminal losses: a car touching the player or dog, and the
 * frenzy meter filling up (see below).
 *
 * ## The frenzy loop
 *
 * `HazardManager` measures one number every frame — how close the nearest
 * critter is to the dog, as a 0..1 pull — and this class fans it out to
 * everything the player can perceive: the dog hauls that much harder, barks
 * that much faster, the lead brightens, and the frenzy meter climbs. Fill the
 * meter and the dog is gone and the run is over. The curves are in the pure
 * `systems/frenzy.ts`; all that happens here is the wiring.
 *
 * ## The guided walk
 *
 * On the tutorial level — and only there — a second, much simpler machine runs
 * alongside the objectives: the lesson script in `systems/tutorial.ts`. This
 * class feeds it what the run looks like (how far the walker has got, what the
 * dog is doing, whether a horn just sounded) and emits the card it returns.
 * Everywhere else `this.tutorial` is null and none of that happens.
 *
 * All HUD text lives in ui/hud.ts; this file only *emits* events (types.ts →
 * GameEvents), keeping simulation and presentation decoupled.
 *
 * Per-frame order matters and is:
 *   input → dog AI → hazards → traffic → leash (writes velocities)
 *   → integrate + collide → animations → camera → render
 * Animations come last because facing and walk/idle are chosen from the *final*
 * velocity the leash produced.
 */
import * as THREE from 'three';
import {
  OBJECTIVE,
  EFFECTS,
  PLAYER,
  DOG,
  LEASH,
  ROADS,
  TREES,
  FRENZY,
  TUTORIAL,
} from '../config/constants';
import { activeLevel } from '../config/level';
import { Effects } from '../render/effects';
import { Player } from '../entities/Player';
import { Dog } from '../entities/Dog';
import { Leash } from '../systems/Leash';
import { HazardManager } from '../systems/HazardManager';
import { homeDoorstep, homeHouseRect } from '../systems/home';
import { GRASS_MARGIN, nearestGrass } from '../systems/grass';
import { atAnyBin, nearestBin } from '../systems/bins';
import { TrafficManager } from '../systems/TrafficManager';
import {
  clampToBounds,
  resolveCircleCircle,
  resolveCircleObstacle,
  resolveCircleRects,
} from '../systems/collision';
import {
  initialObjectiveState,
  notePoop,
  stepObjective,
  endRun,
  type ObjectiveState,
  type ObjectiveEffect,
  type ObjectiveStep,
} from '../systems/objectives';
import {
  initialFrenzy,
  stepFrenzy,
  frenzyMaxed,
  type FrenzyState,
} from '../systems/frenzy';
import {
  finishTutorial,
  holdsUrge,
  initialTutorial,
  sameCard,
  stepTutorial,
  tutorialCard,
  type TutorialCard,
  type TutorialEffect,
  type TutorialState,
} from '../systems/tutorial';
import { carriagewayWidth, isStreet, spansMap } from '../systems/roads';
import { distance } from '../utils/vec2';
import { DogState, Objective, GameEvents, Emitter, type InputScheme } from '../types';
import {
  createRenderer,
  createScene,
  attachResize,
  findSun,
  updateShadowFocus,
  type GameRenderer,
} from '../render/scene';
import { buildWorld, createHomeZone, createMarker, pulseMarker } from '../render/world';
import { CameraRig } from './CameraRig';
import { Input } from './Input';
import { SoundManager } from '../audio/SoundManager';

/** Largest frame delta the sim will accept (ms). See the note in `tick()`. */
const MAX_DELTA_MS = 100;

export class Game {
  readonly events = new Emitter();
  readonly sound: SoundManager;

  private renderer: GameRenderer;
  private scene: THREE.Scene;
  /** The sun, kept so its shadow frustum can be walked along with the camera. */
  private sun: THREE.DirectionalLight | undefined;
  private rig = new CameraRig();
  /** Exposed so the touch controls can feed it their button state. */
  readonly input: Input;
  private detachResize: () => void;

  private player!: Player;
  private dog!: Dog;
  private leash!: Leash;
  private hazards!: HazardManager;
  private traffic!: TrafficManager;
  private effects!: Effects;

  private homeZone!: THREE.Group;
  /** One ring per bin — the town has several, and all of them take the bag. */
  private binMarkers!: THREE.Mesh[];
  private homeMarker!: THREE.Mesh;
  /**
   * Ring on the nearest park, shown while the dog is looking for one.
   *
   * The other two markers belong to an objective and are switched by
   * `showObjective`; this one belongs to the *dog's* state and moves, because
   * the nearest green changes as the pair walk. It is the answer to "where is
   * it going to poop" — a question the town, with its parks tucked behind
   * terraces, otherwise gives the player no way to answer.
   */
  private grassMarker!: THREE.Mesh;

  /**
   * The objective machine's state. The rules themselves live in
   * systems/objectives.ts — this class only applies the effects they return.
   */
  private objectives: ObjectiveState = initialObjectiveState();
  /** The frenzy meter. Rules in systems/frenzy.ts; this only feeds and reads it. */
  private frenzy: FrenzyState = initialFrenzy();
  /**
   * The guided walk, on the one level that has one. `null` everywhere else, and
   * that null is what keeps the tutorial out of every other run: no state, no
   * facts gathered, and one `TUTORIAL` event carrying nothing.
   */
  private tutorial: TutorialState | null = null;
  /** Last lesson announced to the HUD; see `advanceTutorial()`. */
  private lesson: TutorialCard | null = null;
  /** How far the walker has travelled, which is what the first lessons ask for. */
  private walked = 0;
  /** Where they were last frame, to measure that against. */
  private readonly wasAt = { x: 0, y: 0 };
  /** Scratch for reading the steering direction; reused, per the hot-loop rule. */
  private readonly steering = { x: 0, y: 0 };
  /**
   * Did a vehicle sound its horn since the tutorial last looked?
   *
   * The horn already means "a car is about to hit one of you" (systems/horn.ts),
   * so it is exactly the moment the traffic aside is about — and re-deriving
   * that from the vehicle list would be a second, worse copy of the rule.
   */
  private horned = false;
  /** Last control scheme announced to the HUD; see syncInputScheme(). */
  private lastScheme: InputScheme;

  /**
   * The level being played — the town's shape and its objectives, as opposed to
   * the tuning in `constants.ts` that every level shares.
   *
   * Snapshotted once here rather than re-read per frame: the world is built in
   * this constructor, so swapping the level under a running game would move the
   * collision rects out from under the meshes. `config/level.ts` says the same
   * thing from the other side.
   */
  private readonly level = activeLevel();

  /**
   * Everything with walls: the town's plots, plus the walker's own house.
   *
   * The house is not one of the level's buildings — the town is forbidden to
   * build on the home plot, so it never appears in that list — but it is drawn
   * on it and it has to stop you like any other wall. Left out, you walk
   * straight through your own front room on the way back to the doormat.
   */
  private readonly solids = [...this.level.buildings, homeHouseRect(this.level.home)];

  /**
   * The streets a car can actually drive down: carriageways that run clean
   * across the map, which is the same set `planTraffic` is allowed to use.
   *
   * Derived once — it is a walk of the level's roads, and the guided walk asks
   * "am I near one of these" on every frame of its bin lesson.
   */
  private readonly drivableRoads = this.level.roads.filter(
    (road) => isStreet(road) && spansMap(road, this.level.world),
  );

  private lastTime = 0;
  private elapsed = 0;
  /** Frames rendered — handy for confirming the loop is live from the console/tests. */
  frame = 0;
  /** Set by main.ts so pressing R can rebuild a fresh run. */
  onRestart?: () => void;

  /**
   * @param parent        where the canvas is mounted
   * @param makeRenderer  how to build the renderer. Defaults to the real WebGL
   *                      one; tests pass a stand-in, since a GPU context is the
   *                      only thing here that a headless run cannot provide.
   */
  constructor(
    parent: HTMLElement,
    makeRenderer: (parent: HTMLElement) => GameRenderer = createRenderer,
  ) {
    this.renderer = makeRenderer(parent);
    this.scene = createScene();
    this.sun = findSun(this.scene);
    this.input = new Input();
    this.lastScheme = this.input.scheme;
    this.detachResize = attachResize(this.renderer, this.rig.camera);

    this.sound = new SoundManager(this.events);

    buildWorld(this.scene);
    this.homeZone = createHomeZone(this.scene);
    // A ring on every bin, not just the nearest: which one is closest changes
    // as the walk does, and a marker that moved between bins would read as one
    // bin that teleports.
    this.binMarkers = this.level.bins.map((bin) =>
      createMarker(this.scene, bin.x, bin.y, 0xffe08a),
    );
    // On the doormat rather than the middle of the plot: the middle of the plot
    // is inside the house, and a waypoint ring under a building is a ring
    // nobody can see or stand in.
    const doorstep = homeDoorstep(this.level.home);
    this.homeMarker = createMarker(this.scene, doorstep.x, doorstep.y, 0x9dffb0);
    // Parked off-origin until the dog wants it; `showGrassTarget` places it.
    this.grassMarker = createMarker(this.scene, 0, 0, 0xd8f36a);

    // The walker starts where the level says, dog just beside them.
    this.player = new Player(this.level.start.x, this.level.start.y, this.input);
    this.scene.add(this.player.object);
    // Dog starts at heel — inside the leash's slack length, so the walk doesn't
    // open with the lead already taut.
    this.dog = new Dog(this.player.pos.x + LEASH.REST_LENGTH * 0.8, this.player.pos.y, this.player);
    this.scene.add(this.dog.object);

    this.effects = new Effects(this.scene);

    // The poop lands when the *squat* finishes, not when it starts: POOPING is
    // the few seconds of the dog visibly going, POOP_PENDING the moment there
    // is something on the pavement — and the moment the walker starts bagging
    // it, which `notePoop` puts on the clock.
    this.dog.onStateChange = (state: DogState) => {
      this.events.emit(GameEvents.DOG_STATE, state);
      if (state !== DogState.POOP_PENDING) return;
      this.effects.setPoopProgress(0, 0, 0); // the squat ring has served its turn
      this.events.emit(GameEvents.POOP_PROGRESS, 0);
      this.apply(notePoop(this.objectives));
    };
    // A bark is a sound and a pulse at the dog: the ring is what makes the
    // barking legible with the volume off, and it grows with the lunge, so a
    // dog that has properly locked on is unmistakable from across the map.
    //
    // It is also the one thing pigeons care about — the ring and the birds
    // going up are the same event, which is why the flush is triggered here
    // rather than from anything that re-measures how close the dog is.
    this.dog.onBark = (intensity: number) => {
      this.events.emit(GameEvents.SOUND_DOG_BARK, intensity);
      this.effects.ring(
        this.dog.pos.x,
        this.dog.pos.y,
        EFFECTS.FRENZY_COLOR,
        26 + 34 * intensity,
        0.35,
      );
      this.hazards.flushBark(this.dog.pos.x, this.dog.pos.y);
    };

    this.leash = new Leash(this.scene, this.player, this.dog);
    this.hazards = new HazardManager(this.scene, this.dog, this.solids);
    this.hazards.onCritterStartle = (type) => {
      this.events.emit(GameEvents.SOUND_HAZARD_STARTLE, type);
    };
    this.hazards.onCritterSpotted = (type) => {
      this.events.emit(GameEvents.SOUND_CRITTER_SPOTTED, type);
    };
    this.traffic = new TrafficManager(this.scene, this.player, this.dog);
    this.traffic.onHit = () => this.apply(endRun(this.objectives, 'car'));
    this.traffic.onHorn = () => {
      this.horned = true;
      this.events.emit(GameEvents.SOUND_CAR_HORN);
    };

    // The guided walk, if this is the level that has one.
    if (this.level.tutorial) {
      this.tutorial = initialTutorial();
      this.lesson = tutorialCard(this.tutorial);
    }
    this.wasAt.x = this.player.pos.x;
    this.wasAt.y = this.player.pos.y;

    this.rig.snapTo(this.player.pos);
  }

  /**
   * Current objective and whether the run is over. Read by the dev-only
   * `window.__DOGWALKER__` handle that the browser smoke-run drives (see
   * AGENTS.md → Verifying changes), so these stay part of the public surface
   * even though the state itself now lives in the objective machine.
   */
  get objective(): Objective {
    return this.objectives.objective;
  }

  get ended(): boolean {
    return this.objectives.ended;
  }

  /** Push the opening HUD state (call once listeners are attached). */
  emitInitialState(): void {
    this.events.emit(GameEvents.OBJECTIVE, this.objectives.objective);
    this.events.emit(GameEvents.DOG_STATE, this.dog.dogState);
    this.events.emit(GameEvents.FRENZY, 0);
    this.events.emit(GameEvents.POOP_PROGRESS, 0);
    this.events.emit(GameEvents.INPUT_SCHEME, this.input.scheme);
    this.events.emit(GameEvents.PLAYER_POS, this.player.pos);
    // Null on every level but the guided one, which is how the HUD knows not to
    // draw a tutorial panel at all.
    this.events.emit(GameEvents.TUTORIAL, this.lesson);
  }

  /**
   * Re-announce the control scheme when the player switches — picking up a mouse
   * on a touchscreen laptop, or reaching for the keys after dragging. Cheap: a
   * string compare per frame, and an event only on an actual change.
   */
  private syncInputScheme(): void {
    if (this.input.scheme === this.lastScheme) return;
    this.lastScheme = this.input.scheme;
    this.events.emit(GameEvents.INPUT_SCHEME, this.lastScheme);
  }

  start(): void {
    this.lastTime = performance.now();
    this.renderer.setAnimationLoop(() => this.tick());
  }

  // ---- Objective transitions ---------------------------------------------------

  /**
   * Commit a step from the objective machine: adopt the new state, then carry
   * out whatever it asked for. All the *deciding* happens in
   * systems/objectives.ts; everything here is presentation and side effects.
   */
  private apply([next, effects]: ObjectiveStep): void {
    this.objectives = next;
    for (const effect of effects) this.runEffect(effect);
  }

  private runEffect(effect: ObjectiveEffect): void {
    switch (effect.type) {
      case 'objective':
        this.showObjective(effect.objective);
        break;

      case 'poop-landed':
        // Where the dog actually is. It stays rooted through the scoop
        // (`Dog.isBusy`), so the walker cannot tow it off the mess in the
        // second it takes them to bend down.
        this.effects.dropPoop(this.dog.pos.x, this.dog.pos.y);
        break;

      case 'scooped':
        this.dog.markCleaned();
        this.effects.cleanPoop(); // sparkle + ring where the poop was
        // Bag appears in the walker's hand — the whole errand from here is
        // carrying it somewhere.
        this.player.setCarryingBag(true);
        break;

      case 'binned': {
        this.player.setCarryingBag(false);
        // "Binned it" pop at the bin they actually walked to, which on a town
        // with several of them is not necessarily the first one in the list.
        const bin = nearestBin(this.player.pos, this.level.bins) ?? this.player.pos;
        this.effects.burst(bin.x, bin.y, {
          count: 14,
          color: EFFECTS.BIN_COLOR,
          size: 3,
          spread: 70,
          lift: 150,
          life: 0.8,
          height: 34,
        });
        this.effects.ring(bin.x, bin.y, EFFECTS.BIN_COLOR, 70, 0.6);
        break;
      }

      case 'won':
        this.traffic.stop();
        this.endTutorial();
        this.effects.confetti(this.player.pos.x, this.player.pos.y);
        this.events.emit(GameEvents.WON);
        break;

      case 'lost':
        this.traffic.stop();
        this.endTutorial();
        this.player.stop();
        this.dog.stop();
        // A dog that has broken loose leaves a mess where it was standing; a
        // car crash doesn't need one on top of the car.
        if (effect.reason === 'frenzy') {
          this.effects.burst(this.dog.pos.x, this.dog.pos.y, {
            count: 16,
            color: EFFECTS.FRENZY_COLOR,
            size: 3.5,
            spread: 110,
            lift: 150,
            life: 0.9,
            height: 26,
          });
          this.effects.ring(this.dog.pos.x, this.dog.pos.y, EFFECTS.FRENZY_COLOR, 90, 0.6);
        }
        this.events.emit(GameEvents.LOST, effect.reason);
        break;
    }
  }

  /** Marker visibility and the HUD line for an objective change. */
  private showObjective(next: Objective): void {
    for (const marker of this.binMarkers) marker.visible = next === Objective.CARRYING_BAG;
    this.homeMarker.visible = next === Objective.GO_HOME;
    if (next === Objective.GO_HOME) this.homeZone.visible = true;
    this.events.emit(GameEvents.OBJECTIVE, next);
  }

  // ---- Main loop ---------------------------------------------------------------

  private tick(): void {
    const now = performance.now();
    // Clamp the delta so a backgrounded tab doesn't teleport everything on return.
    // The cap doubles as a frame-rate floor: below 1000/MAX_DELTA_MS fps the game
    // runs in slow motion rather than skipping. 100ms (10fps) keeps collision
    // stable while not penalising weak devices the way a tighter cap would.
    const deltaMs = Math.min(now - this.lastTime, MAX_DELTA_MS);
    this.lastTime = now;
    const dt = deltaMs / 1000;
    this.elapsed += dt;
    this.frame++;

    if (this.input.wasRestartPressed()) {
      this.input.endFrame();
      this.onRestart?.();
      return;
    }

    this.syncInputScheme();
    if (!this.objectives.ended) this.simulate(deltaMs, dt);

    // Effects keep animating after the run ends, so the win confetti can play out.
    this.effects.update(dt);
    for (const marker of this.binMarkers) pulseMarker(marker, this.elapsed);
    pulseMarker(this.homeMarker, this.elapsed);
    pulseMarker(this.grassMarker, this.elapsed);
    this.rig.update(this.player.pos, this.player.vel, dt);
    // The shadow frustum is a fixed size and follows what the camera is looking
    // at, so shadow resolution no longer depends on how big the town is. Driven
    // from the rig's smoothed focus rather than the player's position, or the
    // shadows would jitter against a camera that lags behind them.
    if (this.sun) {
      const focus = this.rig.focusPoint;
      updateShadowFocus(this.sun, focus.x, focus.z);
    }
    this.renderer.render(this.scene, this.rig.camera);
    this.input.endFrame();
  }

  private simulate(deltaMs: number, dt: number): void {
    // Before the dog is stepped, not after: the guided walk holds the urge back
    // while it is still explaining the lead, and a hold applied after the fact
    // would arrive a frame late — which is a frame the dog can spend entering
    // SNIFFING. Always false on every other level, where nothing holds anything.
    this.dog.urgeHeld = this.tutorial !== null && holdsUrge(this.tutorial);
    this.dog.update(deltaMs);
    this.hazards.update(deltaMs, dt, this.elapsed);
    this.traffic.update(deltaMs, dt);
    if (this.objectives.ended) return; // traffic may have ended the run this frame

    // The frenzy meter, from the pull the hazards just measured. Stepped before
    // the run can end on it, so the bar the player sees at the game over is the
    // full one that caused it.
    this.stepFrenzyMeter(deltaMs);
    if (this.objectives.ended) return;

    // The lead reads the dog's mood, not its own geometry — see Leash.ts.
    this.leash.strain = this.hazards.pull;
    // Leash resolves both desired velocities into final velocities.
    this.leash.update();

    // Integrate, then collide. Order matters: the walker and dog are pushed apart
    // first, and buildings/bounds get the last word — so a separation that would
    // shove one of them into a wall is undone, rather than the other way round.
    // (In a tight corner that can leave them slightly overlapped for a frame,
    // which is far less objectionable than standing inside a building.)
    this.player.integrate(dt);
    this.dog.integrate(dt);

    this.events.emit(GameEvents.PLAYER_POS, this.player.pos);
    // Note the BODY_RADIUS values rather than the entities' gameplay `radius`:
    // the dog's collision circle is far smaller than its nose-to-tail model, so
    // separating on `radius` would still let the two visibly intersect.
    resolveCircleCircle(
      this.player.pos,
      PLAYER.BODY_RADIUS,
      this.dog.pos,
      DOG.BODY_RADIUS,
      PLAYER.DOG_PUSH_SHARE,
    );
    for (const e of [this.player, this.dog]) {
      resolveCircleRects(e.pos, e.radius, this.solids);
      // Tree trunks are solid; the canopy is not, so you walk under branches.
      for (const t of this.level.trees) {
        resolveCircleObstacle(e.pos, e.radius, t.x, t.y, TREES.TRUNK_RADIUS * t.scale);
      }
      clampToBounds(e.pos, e.radius, this.level.bounds);
    }

    this.player.updateVisuals(dt);
    this.dog.updateVisuals(dt, this.elapsed);
    this.leash.draw();
    this.showGrassTarget();
    this.showPoopProgress();

    // Read the world, then let the objective machine decide what it means.
    this.apply(
      stepObjective(this.objectives, {
        deltaMs,
        // Any bin will do — a town has several. See `systems/bins.ts`.
        atBin: atAnyBin(this.player.pos, this.level.bins, OBJECTIVE.BIN_RADIUS),
        atHome: this.inHomeZone(),
      }),
    );
    // Last, so the lesson is chosen from the objective this frame produced
    // rather than the one it started on.
    this.advanceTutorial(deltaMs);
  }

  /**
   * Step the guided walk, carry out what it asks for, and tell the HUD when the
   * card changes.
   *
   * The card is edge-triggered like the cleanup-range signal, and for the same
   * reason: it changes a handful of times in a run, and a DOM write per frame
   * for it would be a hundred a second saying the same thing.
   */
  private advanceTutorial(deltaMs: number): void {
    if (!this.tutorial) return;

    this.walked += this.walkedThisFrame();

    const [next, effects] = stepTutorial(
      this.tutorial,
      {
        walkedPx: this.walked,
        dogState: this.dog.dogState,
        objective: this.objectives.objective,
        frenzy: this.frenzy.level,
        hornSounding: this.horned,
        nearRoad: this.nearCarriageway(),
      },
      deltaMs,
    );
    this.horned = false; // one frame's worth; the tip has had its look
    this.tutorial = next;
    for (const effect of effects) this.runTutorialEffect(effect);

    const card = tutorialCard(this.tutorial);
    if (sameCard(card, this.lesson)) return;
    this.lesson = card;
    this.events.emit(GameEvents.TUTORIAL, card);
  }

  /**
   * How far the walker has walked since the last frame — **their** walking.
   *
   * Ground actually covered, not intended velocity, so leaning on a key against
   * a wall gets no credit. But only while they are steering: the leash drags the
   * walker about all run, and counting that would have the opening lesson tick
   * over while the player stood still and watched the dog tow them.
   */
  private walkedThisFrame(): number {
    const moved = distance(this.wasAt, this.player.pos);
    this.wasAt.x = this.player.pos.x;
    this.wasAt.y = this.player.pos.y;
    const steer = this.input.getDirection(this.steering);
    return steer.x === 0 && steer.y === 0 ? 0 : moved;
  }

  /**
   * Is the walker close enough to a carriageway to be about to cross it?
   *
   * Streets that run clean across the map only — the same set traffic is
   * allowed on. A lane carries no cars, so being beside one is not a crossing
   * and cueing the guided walk's car off it would send that car somewhere the
   * player is not.
   */
  private nearCarriageway(): boolean {
    return this.drivableRoads.some((road) => {
      const along = road.orient === 'h' ? this.player.pos.y : this.player.pos.x;
      const half = carriagewayWidth(road, ROADS.WIDTH) / 2;
      return Math.abs(along - road.pos) <= half + TUTORIAL.CROSSING_NOTICE_PX;
    });
  }

  private runTutorialEffect(effect: TutorialEffect): void {
    switch (effect.type) {
      case 'hurry-urge':
        this.dog.hurryUrge(TUTORIAL.URGE_WITHIN_MS);
        break;

      case 'release-critter':
        // Near the dog, since the lesson is about what a critter does to it —
        // and `releaseNext` is what keeps that a safe distance rather than a
        // lunge the moment the card appears.
        this.hazards.releaseNext(this.dog.pos);
        break;

      case 'release-vehicle':
        this.releaseTeachingCar();
        break;
    }
  }

  /**
   * Send one car down the street the walker is walking up to.
   *
   * Aimed rather than scheduled, which is the whole point: a car on a timer
   * either passes while they are still on the doorstep or never comes at all,
   * and the crossing is the one lesson that needs the thing it is about to be
   * there. `TrafficManager` spawns off the map edge and honours the level's cap
   * of one vehicle, so this can only ever add a car the player has time to see —
   * and can never add a second one on top of the schedule's.
   */
  private releaseTeachingCar(): void {
    const road = this.nearestDrivable();
    if (road === undefined) return;
    const horizontal = this.level.roads[road].orient === 'h';
    // From whichever end is further away, so it is in view for as long as
    // possible before it arrives. The walker's own position *along* the road
    // decides that: a car coming from the near end would be on them at once.
    const along = horizontal ? this.player.pos.x : this.player.pos.y;
    const extent = horizontal ? this.level.world.width : this.level.world.height;
    this.traffic.spawnVehicle({ atMs: 0, road, dir: along > extent / 2 ? 1 : -1, kind: 'car' });
  }

  /** Index of the drivable street nearest the walker, if there is one. */
  private nearestDrivable(): number | undefined {
    let best: number | undefined;
    let bestGap = Infinity;
    for (const road of this.drivableRoads) {
      const index = this.level.roads.indexOf(road);
      const along = road.orient === 'h' ? this.player.pos.y : this.player.pos.x;
      const gap = Math.abs(along - road.pos);
      if (gap < bestGap) {
        bestGap = gap;
        best = index;
      }
    }
    return best;
  }

  /** Take the guided walk's card down, however the run ended. */
  private endTutorial(): void {
    if (!this.tutorial || this.lesson === null) return;
    this.tutorial = finishTutorial(this.tutorial);
    this.lesson = null;
    this.events.emit(GameEvents.TUTORIAL, null);
  }

  /**
   * Step the frenzy meter and end the run if it tops out.
   *
   * The input is the hazards' pull — the same number the dog is lunging and
   * barking at — so the bar is never telling a different story from the animal
   * it is measuring.
   */
  private stepFrenzyMeter(deltaMs: number): void {
    const before = this.frenzy.level;
    this.frenzy = stepFrenzy(this.frenzy, this.hazards.pull, deltaMs, FRENZY);
    if (this.frenzy.level !== before) this.events.emit(GameEvents.FRENZY, this.frenzy.level);
    if (frenzyMaxed(this.frenzy)) this.apply(endRun(this.objectives, 'frenzy'));
  }

  /**
   * Put a ring on the grass the dog is heading for, for as long as it wants one.
   *
   * Re-aimed every frame rather than latched when the urge lands: the walker is
   * dragging the dog around the whole time, so the nearest park genuinely
   * changes on the way — and a ring left on the park they have just walked away
   * from is worse than no ring at all. `Dog.update` picks its target the same
   * way, so the marker and the animal always agree.
   */
  private showGrassTarget(): void {
    const seeking = this.dog.seekingGrass;
    this.grassMarker.visible = seeking;
    if (!seeking) return;
    const spot = nearestGrass(this.dog.pos, this.level.parks, GRASS_MARGIN);
    // A level with no grass at all is one the validator refuses to ship; if one
    // ever reached a player, the ring simply stays where it was.
    if (spot) this.grassMarker.position.set(spot.x, this.grassMarker.position.y, spot.y);
  }

  /** Mirror the dog's squat as a ring in the world and a meter in the HUD. */
  private showPoopProgress(): void {
    const ratio = this.dog.poopProgress;
    if (ratio <= 0) return; // the state change clears both; nothing to say here
    this.effects.setPoopProgress(this.dog.pos.x, this.dog.pos.y, ratio);
    this.events.emit(GameEvents.POOP_PROGRESS, ratio);
  }

  private inHomeZone(): boolean {
    const { x, y, w, h } = this.level.home;
    const p = this.player.pos;
    return p.x >= x && p.x <= x + w && p.y >= y && p.y <= y + h;
  }

  dispose(): void {
    this.renderer.setAnimationLoop(null);
    this.detachResize();
    this.sound.dispose();
    this.effects.dispose();
    this.input.dispose();
    this.events.removeAll();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
